import React, { useState, useEffect, useRef } from "react";
import { Toaster, toast } from "sonner";
import { 
  Check, 
  Calendar, 
  Clock, 
  Sparkles, 
  BookOpen, 
  Users, 
  ChevronDown, 
  MapPin, 
  X, 
  ChevronRight, 
  TrendingUp, 
  Gift, 
  Flame, 
  Compass, 
  Heart,
  ExternalLink
} from "lucide-react";

// Canonical configuration and constant assets
const WHATSAPP_GROUP_URL = "https://chat.whatsapp.com/GL2HfCiODxoBi5nQ33852o?s=cl&p=i&mlu=4&ilr=4&iam=2";

const BRAND_IMAGES = {
  spiced_truffles: "https://tagmango.com/assets/christmas-carousel-202511041343310002-f709c55e12886bb71767c634d9151b62.png",
  alcoholic_sangria: "https://tagmango.com/assets/christmas-carousel-202511041343330009-4d9d310c00522cbe8ce6d8f297e27a70.png",
  masterclass_cover: "https://tagmango.com/assets/christmas-carousel-202511041343300000-effc16eabdcf0a572ac61401ef9512e4.png",
  chocolate_walnut_pie: "https://tagmango.com/assets/christmas-carousel-202511041343340017-1341d23faa29e29cd99735f428f8c421.png",
  christmas_cookies: "https://tagmango.com/assets/christmas-carousel-202511041343310005-1f39b7405e85c86f4e20eb08efc99595.png",
  chef_profile: "https://tagmango.com/assets/img3001-2-1a09d3e3a4165108ee66ebc2aa759851.JPG",
  hot_chocolate: "https://tagmango.com/assets/img2029-e926574401b6acbdbec211eaa1374ee9.JPG",
  gourmet_cake: "https://tagmango.com/assets/img9924-0a698a44691c9d114cca0371a98cfeb3.JPG",
  alternate_pie: "https://tagmango.com/assets/christmas-carousel-202511041343330010-89d7d91a2c2632c6007385487ab941d6.png"
};

// Sub-component: Individual FAQ Accordion Item
const FAQItem = ({ question, answer, index }) => {
  const [isOpen, setIsOpen] = useState(false);
  return (
    <div className="border-b border-[#E2E8F0] py-4">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex justify-between items-center text-left py-2 group focus:outline-none"
        data-testid={`faq-item-${index}`}
        aria-expanded={isOpen}
      >
        <span className="font-sans font-medium text-lg text-[#0F172A] group-hover:text-[#991B1B] transition-colors">
          {question}
        </span>
        <ChevronDown 
          className={`w-5 h-5 text-slate-400 group-hover:text-[#991B1B] transition-transform duration-300 ${
            isOpen ? "transform rotate-180" : ""
          }`} 
        />
      </button>
      <div 
        className={`grid transition-all duration-300 ease-in-out ${
          isOpen ? "grid-rows-[1fr] opacity-100 mt-2" : "grid-rows-[0fr] opacity-0"
        }`}
      >
        <div className="overflow-hidden">
          <p className="font-sans text-slate-600 leading-relaxed pb-2 text-base">
            {answer}
          </p>
        </div>
      </div>
    </div>
  );
};

// Sub-component: Day curriculum list renderer
const DayPanel = ({ bakes, concepts, businessTitle, businessItems, highlightText }) => (
  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
    <div>
      <h4 className="font-serif font-semibold text-[#0F172A] text-xl mb-4 flex items-center space-x-2">
        <span className="text-[#991B1B]">🍪</span>
        <span>Signature Recipes</span>
      </h4>
      <div className="space-y-4">
        {bakes.map((item, idx) => (
          <div key={idx} className="p-4 bg-[#FAFAFA] rounded-xl border border-slate-100 flex items-start space-x-3 hover:border-rose-200 transition-colors">
            <Check className="w-5 h-5 text-[#D97706] shrink-0 mt-0.5" />
            <div>
              <span className="font-sans font-semibold text-sm text-[#0F172A] block">{item.name}</span>
              <span className="font-sans text-xs text-slate-500">{item.desc}</span>
            </div>
          </div>
        ))}
      </div>
    </div>

    <div className="bg-[#FAFAFA] p-8 rounded-2xl border border-slate-100 flex flex-col justify-between">
      <div>
        <h4 className="font-serif font-semibold text-[#0F172A] text-xl mb-4 flex items-center space-x-2">
          <span className="text-[#D97706]">🧠</span>
          <span>{businessTitle || "Key Concepts You'll Learn"}</span>
        </h4>
        <div className="space-y-4">
          {(concepts || businessItems).map((item, idx) => (
            <div key={idx} className="flex items-start space-x-3">
              <div className="w-5 h-5 rounded-full bg-amber-50 text-[#D97706] flex items-center justify-center text-xs font-bold shrink-0 mt-0.5">
                {idx + 1}
              </div>
              <span className="font-sans text-sm text-slate-600 leading-relaxed">{item}</span>
            </div>
          ))}
        </div>
      </div>

      <div className="mt-8 p-4 bg-[#FEF3C7] rounded-xl border border-[#FDE68A] flex items-center space-x-3">
        <Sparkles className="w-5 h-5 text-[#D97706] shrink-0" />
        <span className="font-sans text-xs font-medium text-amber-900">
          {highlightText}
        </span>
      </div>
    </div>
  </div>
);

export default function App() {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [formData, setFormData] = useState({ name: "", email: "", phone: "" });
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [activeTab, setActiveTab] = useState("day1");
  const redirectTimeoutRef = useRef(null);

  // Close modal with Escape key
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === "Escape") {
        handleCloseModal();
      }
    };
    if (isModalOpen) {
      window.addEventListener("keydown", handleKeyDown);
    }
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [isModalOpen]);

  // Clean up redirect timer on unmount
  useEffect(() => {
    return () => {
      if (redirectTimeoutRef.current) {
        clearTimeout(redirectTimeoutRef.current);
      }
    };
  }, []);

  const handleOpenModal = () => {
    if (redirectTimeoutRef.current) {
      clearTimeout(redirectTimeoutRef.current);
    }
    setFormData({ name: "", email: "", phone: "" });
    setIsSubmitted(false);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    if (redirectTimeoutRef.current) {
      clearTimeout(redirectTimeoutRef.current);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!formData.name.trim() || !formData.email.trim() || !formData.phone.trim()) {
      toast.error("Please fill in all details to claim your ticket!");
      return;
    }

    // Save lead locally to simulate checkout persistence
    try {
      const existingLeads = JSON.parse(localStorage.getItem("masterclass_leads") || "[]");
      existingLeads.push({
        ...formData,
        timestamp: new Date().toISOString(),
        id: Math.random().toString(36).substr(2, 9)
      });
      localStorage.setItem("masterclass_leads", JSON.stringify(existingLeads));
    } catch (err) {
      console.warn("Storage write skipped", err);
    }

    setIsSubmitted(true);
    toast.success("Spot Reserved! Access link is ready.");

    redirectTimeoutRef.current = setTimeout(() => {
      window.open(WHATSAPP_GROUP_URL, "_blank", "noopener,noreferrer");
    }, 2500);
  };

  // Static curriculum dataset
  const day1Bakes = [
    { name: "Classic Plum Cake – Alcoholic 🍷", desc: "Rich with dry fruits steeped in rum and premium holiday spices." },
    { name: "Classic Plum Cake – Non-Alcoholic 🥂", desc: "Equally moist option infused with orange juices and winter spice zest." },
    { name: "Plum Cake – Whole Wheat 🌾", desc: "Healthy alternative prepared with organic whole wheat and natural unrefined sugars." },
    { name: "Plum Cake – Gluten-Free 🌾✨", desc: "A soft, spongy, and incredibly moist holiday plum cake prepared entirely with wholesome millet blends." },
    { name: "Chocolate Plum Cake 🍫", desc: "Decadent fusion of rich cocoa solids with steeped festive fruits and holiday spices." },
    { name: "Ginger Cookies 🍪", desc: "Snappy classic gingerbread cookies with dry ginger and dark cinnamon." },
    { name: "Linzer Cookies 🍓", desc: "Elegant hazelnut dough cookies sandwiching sweet ruby-raspberry jam." },
    { name: "Crinkle Cookies ❄️", desc: "Velvety, soft-centered chocolate cookies rolled in sugar for a crackled snow finish." },
    { name: "Christmas Wreath Cookies 🎄", desc: "Crisp vanilla shortbread decorated to replicate gorgeous holiday wreaths." },
    { name: "Starry Night Cookies ⭐", desc: "Crisp cocoa and vanilla layered cookies mimicking a star-filled night sky." },
    { name: "Hot Chocolate (Healthy & Indulgent) ☕", desc: "Luxuriously thick chocolate beverage sweetened without refined sugars." },
    { name: "Sangria – Alcoholic 🍹", desc: "Warmly spiced wine sangria with orange zest and star anise." },
    { name: "Sangria – Virgin 🍊", desc: "Rich mocktail variant spiced with pomegranate juice, orange slices, and mint." }
  ];

  const day1Concepts = [
    "Ancient Millets Science: Unlocking healthy organic grains for premium baking",
    "Gluten vs Gluten-Free: Mastering stability, crumb texture, and bind",
    "Unrefined Sweeteners: Utilizing molasses & organic jaggery for authentic Christmas depth",
    "Cookie Thickness and Spread: Temperature controls for crispy edges & soft center",
    "No-Oven Perfect Heat: Setting up Kadhais & Cookers for flawless gas stove baking"
  ];

  const day2Bakes = [
    { name: "Carrot Cake with Cream Cheese Frosting 🥕", desc: "Super moist eggless crumb covered in homemade unrefined cream cheese frosting." },
    { name: "Chocolate Ganache Cake 🍫", desc: "Layers of dense chocolate cake glazed in satin dark chocolate ganache." },
    { name: "Rum Balls – Alcoholic 🍸", desc: "Decadent rum-infused chocolate spheres, ideal for high-end luxury hampers." },
    { name: "Rum Balls – Non-Alcoholic 🥛", desc: "Delightful chocolate fudge holiday spheres suitable for children and families." },
    { name: "Spiced Christmas Truffles 🎄", desc: "Hand-rolled ganache bites infused with warm clove, cinnamon, and nutmeg notes." },
    { name: "Apple Cinnamon Pie 🥧", desc: "Flaky golden crust packed with spiced caramelised apple filling." },
    { name: "Chocolate Walnut Pie 🥧", desc: "Nutty, dense walnut filling coupled with clean dark chocolate notes." },
    { name: "Cherry Compote 🍒", desc: "Tart holiday cherry compote for cake layered fillings and toppings." },
    { name: "Snow Gobble ❄️", desc: "Airy, melt-in-the-mouth, eggless snow dessert resembling soft winter meringue." },
    { name: "Caramel Flan 🍮", desc: "Silky, eggless custom custard glazed under a perfect golden-amber caramel strip." },
    { name: "Chocolate Flan 🍫🍮", desc: "Rich cocoa variant of the traditional velvety caramel custard." }
  ];

  const day2Business = [
    "Luxury Packaging & Styling: Crafting elite Christmas boxes and gift hampers",
    "Hamper Customization: Designing custom product combinations and holiday corporate orders"
  ];

  const bonuses = [
    { title: "Lifetime Recording Access", desc: "Never forget a step! Learn at your own convenience with lifetime access to high-definition recordings of both sessions.", icon: Clock },
    { title: "Exclusive WhatsApp Support", desc: "Join an active community of bakers for direct Chef feedback, troubleshooting help, and ingredient vendor recommendations.", icon: Users },
    { title: "Printable Recipe eBook PDF", desc: "Beautifully formatted recipe digital booklet with precise metrics, weight conversions, and step-by-step methods.", icon: BookOpen }
  ];

  const galleryImages = [
    { url: BRAND_IMAGES.christmas_cookies, title: "Artisanal Christmas Cookies Box", desc: "Beautifully shaped cookies with zero maida." },
    { url: BRAND_IMAGES.chocolate_walnut_pie, title: "Chocolate Walnut Pie", desc: "Buttery crust packed with toasted walnut crunch." },
    { url: BRAND_IMAGES.spiced_truffles, title: "Spiced Christmas Truffles", desc: "Indulgent hand-rolled chocolate holiday truffles." },
    { url: BRAND_IMAGES.hot_chocolate, title: "Star Hot Chocolate", desc: "Velvety thick drinking cocoa topped with holiday shortbread." },
    { url: BRAND_IMAGES.gourmet_cake, title: "Wholesome Whole-Wheat Cake", desc: "Moist, beautifully crumbed cake topped with clean frosting." },
    { url: BRAND_IMAGES.alcoholic_sangria, title: "Festive Drinks & Desserts", desc: "Perfect seasonal pairing for cozy winter bakes." }
  ];

  const faqs = [
    { q: "Are the baking ingredients easily available?", a: "Yes, 100%! All organic millet flours, unrefined sweeteners, and spices used by Chef Ashmika are standard grocery items. You can easily find them in local health food stores, supermarkets, or online portals." },
    { q: "I have zero baking experience. Can I join?", a: "Absolutely! The entire masterclass is designed with complete beginners in mind. We teach basic measurements, flour mixing methods, and baking techniques step-by-step." },
    { q: "How do we bake on a gas stove without an OTG?", a: "We show you how to set up a standard thick-bottomed cooker or pot as an oven. This heat-convection technique ensures your cakes bake perfectly moist and rise beautifully!" },
    { q: "Are these recipes completely healthy and guilt-free?", a: "Yes. Every recipe is meticulously structured to omit gluten, maida, white refined sugar, and eggs. We rely on wholesome millet flours, molasses, and dates to preserve nutritional value and authentic taste." }
  ];

  return (
    <div className="min-h-screen bg-[#FAFAFA] text-[#0F172A] selection:bg-[#FCA5A5] selection:text-[#991B1B]">
      <Toaster position="top-center" richColors closeButton />

      {/* Sticky Premium Header */}
      <header className="sticky top-0 z-50 glass-header">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-20 flex items-center justify-between animate-fadeIn">
          
          {/* Logo / Brand Name */}
          <div className="flex items-center space-x-2.5">
            <span className="text-[#991B1B] text-2xl">🎄</span>
            <div className="flex flex-col">
              <span className="font-serif font-bold text-xl tracking-tight text-[#0F172A]">CHEF ASHMIKA</span>
              <span className="text-[10px] tracking-[0.25em] text-[#991B1B] font-bold">BAKING ACADEMY</span>
            </div>
          </div>

          {/* Center Navigation Links */}
          <nav className="hidden md:flex items-center space-x-8">
            <a href="#curriculum" data-testid="nav-curriculum" className="font-sans text-sm font-medium text-slate-600 hover:text-[#991B1B] transition-colors">Curriculum</a>
            <a href="#bonuses" data-testid="nav-bonuses" className="font-sans text-sm font-medium text-slate-600 hover:text-[#991B1B] transition-colors">Bonuses</a>
            <a href="#instructor" data-testid="nav-instructor" className="font-sans text-sm font-medium text-slate-600 hover:text-[#991B1B] transition-colors">Meet the Chef</a>
            <a href="#gallery" data-testid="nav-gallery" className="font-sans text-sm font-medium text-slate-600 hover:text-[#991B1B] transition-colors">Gallery</a>
            <a href="#faq" data-testid="nav-faq" className="font-sans text-sm font-medium text-slate-600 hover:text-[#991B1B] transition-colors">FAQs</a>
          </nav>

          {/* Date Badge replaced with generic Limited Seats Badge */}
          <div className="flex items-center space-x-4">
            <div className="hidden sm:flex items-center space-x-2 bg-amber-50 border border-amber-200 px-3 py-1.5 rounded-full badge-gold-glow">
              <span className="relative flex h-2.5 w-2.5">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#991B1B] opacity-75"></span>
                <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-[#991B1B]"></span>
              </span>
              <span className="font-sans text-xs font-bold text-[#991B1B] tracking-wider uppercase">LIMITED SEATS AVAILABLE</span>
            </div>

            <button
              onClick={handleOpenModal}
              data-testid="register-button-header"
              className="bg-[#991B1B] text-white hover:bg-[#7F1D1D] px-5 py-2.5 rounded-full font-sans text-sm font-semibold transition-all duration-300 transform active:scale-95 shadow-md shadow-[#991B1B]/10 hover:shadow-lg hover:shadow-[#991B1B]/20"
            >
              Book Spot
            </button>
          </div>
        </div>
      </header>

      <main>
        {/* HERO SECTION */}
        <section className="relative overflow-hidden py-16 lg:py-24 border-b border-[#E2E8F0] bg-gradient-to-b from-[#FAFAFA] to-white">
          <div className="absolute top-10 left-10 pointer-events-none opacity-20 text-[#991B1B] text-xl">❄️</div>
          <div className="absolute top-20 right-20 pointer-events-none opacity-20 text-[#991B1B] text-2xl">❄️</div>
          <div className="absolute bottom-12 left-1/4 pointer-events-none opacity-10 text-[#991B1B] text-3xl">❄️</div>

          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
              
              {/* Left Column Text */}
              <div className="lg:col-span-7 flex flex-col space-y-6 text-left animate-fadeIn">
                
                <div className="inline-flex items-center space-x-2 bg-[#FEF3C7] border border-[#FDE68A] text-[#B45309] px-3.5 py-1.5 rounded-full self-start">
                  <Sparkles className="w-4 h-4 text-[#D97706] animate-pulse" />
                  <span className="font-sans text-xs font-bold uppercase tracking-wider">2-Day Christmas Baking Masterclass</span>
                </div>

                <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-[#0F172A] leading-[1.1]">
                  Bake Wholesome & <br />
                  <span className="text-[#991B1B] relative inline-block">
                    Indulgent Desserts
                    <span className="absolute bottom-1 left-0 w-full h-1 bg-[#D97706]/40 rounded"></span>
                  </span>
                </h1>

                <p className="font-sans text-lg sm:text-xl leading-relaxed text-slate-600 max-w-2xl">
                  Bake, Sip & Celebrate the Spirit of Baking! Join <strong className="text-[#0F172A] font-semibold">Chef Ashmika Agrawal</strong> for a 2-day workshop to learn healthy, clean, and luxurious bakes.
                </p>

                {/* Quick Checklist */}
                <div className="py-2 border-y border-slate-100 grid grid-cols-2 sm:grid-cols-4 gap-4">
                  <div className="flex items-center space-x-2">
                    <Check className="w-5 h-5 text-[#991B1B] shrink-0" />
                    <span className="font-sans text-sm font-semibold text-slate-700">No Gluten • No Maida</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Check className="w-5 h-5 text-[#991B1B] shrink-0" />
                    <span className="font-sans text-sm font-semibold text-slate-700">Eggless (100% Veg)</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Check className="w-5 h-5 text-[#991B1B] shrink-0" />
                    <span className="font-sans text-sm font-semibold text-slate-700">No Refined Sugar</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Check className="w-5 h-5 text-[#991B1B] shrink-0" />
                    <span className="font-sans text-sm font-semibold text-slate-700">Gas Stove Friendly</span>
                  </div>
                </div>

                {/* CTA Buttons */}
                <div className="flex flex-col sm:flex-row items-stretch sm:items-center space-y-3 sm:space-y-0 sm:space-x-4 pt-4">
                  <button
                    onClick={handleOpenModal}
                    data-testid="register-button-hero"
                    className="bg-[#991B1B] hover:bg-[#7F1D1D] text-white text-base font-bold py-4 px-8 rounded-full font-sans transition-all duration-300 transform active:scale-95 shadow-lg shadow-[#991B1B]/20 flex items-center justify-center space-x-2 group"
                  >
                    <span>Reserve My Ticket</span>
                    <ChevronRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
                  </button>

                  <div className="flex flex-col text-center sm:text-left justify-center">
                    <span className="text-xs uppercase tracking-widest text-[#D97706] font-bold">Upcoming Masterclass</span>
                    <span className="text-sm font-medium text-slate-500">Lifetime Video Recording Access</span>
                  </div>
                </div>

                {/* Date removed, only time and mode displayed */}
                <div className="flex items-center space-x-6 pt-4 text-slate-500 text-sm">
                  <div className="flex items-center space-x-1.5">
                    <Clock className="w-4 h-4 text-slate-400" />
                    <span>3:00 PM IST Onwards</span>
                  </div>
                  <div className="flex items-center space-x-1.5">
                    <MapPin className="w-4 h-4 text-slate-400" />
                    <span>Online via ZOOM Sessions</span>
                  </div>
                </div>

              </div>

              {/* Right Column Cover Card */}
              <div className="lg:col-span-5 flex justify-center animate-fadeIn" style={{ animationDelay: "100ms" }}>
                <div className="relative group max-w-md w-full">
                  <div className="absolute inset-0 bg-[#991B1B]/5 rounded-3xl transform rotate-3 scale-102 transition-transform duration-300 group-hover:rotate-1"></div>
                  <div className="absolute inset-0 bg-amber-500/5 rounded-3xl transform -rotate-3 scale-102 transition-transform duration-300 group-hover:-rotate-1"></div>
                  
                  <div className="relative bg-white p-4 rounded-3xl shadow-xl border border-slate-100">
                    <img 
                      src={BRAND_IMAGES.masterclass_cover} 
                      alt="Chef Ashmika Masterclass Cover Header"
                      className="w-full h-auto rounded-2xl object-cover"
                    />
                    <div className="absolute top-6 right-6 bg-[#991B1B] text-white text-[10px] font-bold tracking-[0.2em] uppercase px-3 py-1 rounded-full shadow">
                      ZOOM SESSIONS
                    </div>
                  </div>
                </div>
              </div>

            </div>
          </div>
        </section>

        {/* PILLARS SECTION */}
        <section className="py-16 bg-[#FAFAFA] border-b border-[#E2E8F0]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            
            <div className="max-w-3xl mx-auto mb-12">
              <span className="text-xs uppercase tracking-[0.25em] text-[#991B1B] font-bold block mb-2">Our Core Standards</span>
              <h2 className="font-serif text-3xl sm:text-4xl font-bold text-[#0F172A] tracking-tight">
                Wholesome Baking Pillars
              </h2>
              <div className="w-12 h-1 bg-[#D97706] mx-auto mt-4 rounded"></div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
              
              <div className="premium-card p-8 rounded-2xl text-left bg-white group">
                <div className="w-12 h-12 bg-rose-50 rounded-full flex items-center justify-center text-[#991B1B] mb-5 group-hover:bg-[#991B1B] group-hover:text-white transition-all">
                  <Sparkles className="w-6 h-6" />
                </div>
                <h3 className="font-sans font-semibold text-lg text-[#0F172A] mb-2">100% Gluten-Free</h3>
                <p className="font-sans text-sm text-slate-500 leading-relaxed">
                  Entirely maida-free. Learn to tap into organic millets and premium nutrient-rich starch blends.
                </p>
              </div>

              <div className="premium-card p-8 rounded-2xl text-left bg-white group">
                <div className="w-12 h-12 bg-rose-50 rounded-full flex items-center justify-center text-[#991B1B] mb-5 group-hover:bg-[#991B1B] group-hover:text-white transition-all">
                  <Heart className="w-6 h-6" />
                </div>
                <h3 className="font-sans font-semibold text-lg text-[#0F172A] mb-2">0% Refined Sugar</h3>
                <p className="font-sans text-sm text-slate-500 leading-relaxed">
                  Sweetened naturally with rich molasses, dates, and unrefined jaggery for amazing color and safety.
                </p>
              </div>

              <div className="premium-card p-8 rounded-2xl text-left bg-white group">
                <div className="w-12 h-12 bg-rose-50 rounded-full flex items-center justify-center text-[#991B1B] mb-5 group-hover:bg-[#991B1B] group-hover:text-white transition-all">
                  <Compass className="w-6 h-6" />
                </div>
                <h3 className="font-sans font-semibold text-lg text-[#0F172A] mb-2">Pure Vegetarian</h3>
                <p className="font-sans text-sm text-slate-500 leading-relaxed">
                  No eggs utilized. Perfected vegan techniques to achieve fluffy rises and rich, velvety moist crumbs.
                </p>
              </div>

              <div className="premium-card p-8 rounded-2xl text-left bg-white group">
                <div className="w-12 h-12 bg-rose-50 rounded-full flex items-center justify-center text-[#991B1B] mb-5 group-hover:bg-[#991B1B] group-hover:text-white transition-all">
                  <Flame className="w-6 h-6" />
                </div>
                <h3 className="font-sans font-semibold text-lg text-[#0F172A] mb-2">Bake on Gas Stove</h3>
                <p className="font-sans text-sm text-slate-500 leading-relaxed">
                  No oven? Bake flawlessly inside heavy pots and pressure cookers with perfect heat techniques.
                </p>
              </div>

            </div>
          </div>
        </section>

        {/* CURRICULUM SECTION */}
        <section id="curriculum" className="py-20 bg-white border-b border-[#E2E8F0]">
          <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
            
            <div className="text-center max-w-2xl mx-auto mb-12">
              <span className="text-xs uppercase tracking-[0.25em] text-[#991B1B] font-bold block mb-2">Detailed Syllabus</span>
              <h2 className="font-serif text-3xl sm:text-4xl font-bold text-[#0F172A] tracking-tight">
                Your 2-Day Creative Journey
              </h2>
              <div className="w-12 h-1 bg-[#D97706] mx-auto mt-4 rounded"></div>
            </div>

            {/* Curriculum Tab Buttons */}
            <div className="flex justify-center p-1 bg-slate-100 rounded-full mb-12 max-w-lg mx-auto">
              <button
                onClick={() => setActiveTab("day1")}
                data-testid="tab-day1"
                className={`flex-1 py-3 px-4 sm:px-6 text-sm font-semibold rounded-full transition-all duration-300 ${
                  activeTab === "day1" 
                    ? "bg-[#991B1B] text-white shadow animate-fadeIn" 
                    : "text-slate-600 hover:text-[#991B1B]"
                }`}
              >
                🍪 Day 1: Cakes & Cookies
              </button>
              <button
                onClick={() => setActiveTab("day2")}
                data-testid="tab-day2"
                className={`flex-1 py-3 px-4 sm:px-6 text-sm font-semibold rounded-full transition-all duration-300 ${
                  activeTab === "day2" 
                    ? "bg-[#991B1B] text-white shadow animate-fadeIn" 
                    : "text-slate-600 hover:text-[#991B1B]"
                }`}
              >
                🎂 Day 2: Cakes & Desserts
              </button>
            </div>

            {/* Tab Body */}
            {activeTab === "day1" ? (
              <div className="space-y-10 animate-fadeIn">
                <div className="bg-rose-50/50 border border-rose-100 p-6 rounded-2xl flex flex-col sm:flex-row justify-between items-start sm:items-center">
                  <div>
                    <h3 className="font-sans font-bold text-[#991B1B] text-lg sm:text-xl">Day 1 – Festive Foundations, Cakes & Cookies</h3>
                    <p className="font-sans text-sm text-slate-500">Perfect snappy gingerbread, gorgeous crinkles, Austrian linzers, starry bakes, signature plum cakes, and spiced holiday sips.</p>
                  </div>
                  <span className="mt-3 sm:mt-0 inline-block bg-[#991B1B] text-white text-xs font-bold px-3 py-1.5 rounded-full uppercase tracking-wider shrink-0">
                    {day1Bakes.length} Recipes Included
                  </span>
                </div>
                
                <DayPanel 
                  bakes={day1Bakes} 
                  concepts={day1Concepts} 
                  highlightText="Pro-Tip: All holiday cookies are engineered for perfect structural stability, allowing easy packing for premium gifting kits!"
                />
              </div>
            ) : (
              <div className="space-y-10 animate-fadeIn">
                <div className="bg-rose-50/50 border border-rose-100 p-6 rounded-2xl flex flex-col sm:flex-row justify-between items-start sm:items-center">
                  <div>
                    <h3 className="font-sans font-bold text-[#991B1B] text-lg sm:text-xl">Day 2 – Gourmet Cakes, Pies & Luxurious Desserts</h3>
                    <p className="font-sans text-sm text-slate-500">Learn moist ganache cakes, fruit-loaded holiday pies, spiced truffles, rich flans, and luxury packaging hampers.</p>
                  </div>
                  <span className="mt-3 sm:mt-0 inline-block bg-[#991B1B] text-white text-xs font-bold px-3 py-1.5 rounded-full uppercase tracking-wider shrink-0">
                    {day2Bakes.length} Recipes + Commercial Guides
                  </span>
                </div>

                <DayPanel 
                  bakes={day2Bakes} 
                  businessTitle="The Business Edge Module"
                  businessItems={day2Business} 
                  highlightText="Pro-Tip: Receive a list of certified vendors for food-grade boxes, glass mason jars, custom stamps, and luxury ribbons!"
                />
              </div>
            )}

            {/* Section secondary CTA */}
            <div className="mt-12 text-center">
              <button
                onClick={handleOpenModal}
                data-testid="register-button-curriculum"
                className="bg-[#991B1B] hover:bg-[#7F1D1D] text-white font-sans text-sm font-bold py-3.5 px-8 rounded-full transition-all transform active:scale-95 shadow"
              >
                Enroll & Book Spot
              </button>
            </div>

          </div>
        </section>

        {/* BRANDS & PASTRY SHOWCASE GALLERY */}
        <section id="gallery" className="py-20 bg-[#FAFAFA] border-b border-[#E2E8F0]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            
            <div className="text-center max-w-3xl mx-auto mb-16">
              <span className="text-xs uppercase tracking-[0.25em] text-[#991B1B] font-bold block mb-2">Visual Treats</span>
              <h2 className="font-serif text-3xl sm:text-4xl font-bold text-[#0F172A] tracking-tight">
                Actual Masterclass Creations
              </h2>
              <div className="w-12 h-1 bg-[#D97706] mx-auto mt-4 rounded"></div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {galleryImages.map((img, idx) => (
                <div key={idx} className="bg-white rounded-3xl overflow-hidden shadow-sm border border-slate-100 hover:shadow-md transition-all duration-300">
                  <div className="aspect-[4/5] w-full overflow-hidden bg-slate-100">
                    <img 
                      src={img.url} 
                      alt={img.title}
                      className="w-full h-full object-cover transition-transform duration-500 hover:scale-103"
                      loading="lazy"
                    />
                  </div>
                  <div className="p-5 text-left">
                    <h3 className="font-sans font-semibold text-base text-[#0F172A]">{img.title}</h3>
                    <p className="font-sans text-xs text-slate-500 mt-1">{img.desc}</p>
                  </div>
                </div>
              ))}
            </div>

          </div>
        </section>

        {/* MEET INSTRUCTOR */}
        <section id="instructor" className="py-20 bg-white border-b border-[#E2E8F0]">
          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
              
              <div className="lg:col-span-5 flex justify-center animate-fadeIn">
                <div className="relative max-w-md w-full">
                  <div className="absolute inset-0 bg-[#991B1B]/5 rounded-3xl transform -rotate-3 scale-102"></div>
                  <div className="relative bg-white p-4 rounded-3xl shadow-lg border border-slate-100">
                    <img 
                      src={BRAND_IMAGES.chef_profile} 
                      alt="Chef Ashmika Agrawal Santa Claus profile"
                      className="w-full h-[450px] object-cover rounded-2xl"
                    />
                  </div>
                </div>
              </div>

              <div className="lg:col-span-7 text-left space-y-6">
                <span className="text-xs uppercase tracking-[0.25em] text-[#991B1B] font-bold block mb-2">Mentor & Educator</span>
                <h2 className="font-serif text-3xl sm:text-4xl font-bold text-[#0F172A]">Chef Ashmika Agrawal</h2>
                
                <p className="font-sans text-lg text-slate-600 leading-relaxed">
                  Chef Ashmika is a pioneer in premium vegetarian and gluten-free baking. Her core mission is to deconstruct classic world-class desserts and recreate them utilizing nutrient-dense ancient grains, healthy unrefined starches, and zero refined white sugars.
                </p>

                <p className="font-sans text-base text-slate-500 leading-relaxed">
                  In this Zoom masterclass, she brings her perfected recipes and luxury hamper blueprints straight to your kitchen. Over the past 3 years, she has guided over 5k+ students and cafe operators global-wide to create clean and highly profitable holiday pastry boxes.
                </p>

                <div className="grid grid-cols-3 gap-6 pt-4 border-t border-slate-100">
                  <div>
                    <span className="font-serif text-3xl font-bold text-[#991B1B] block">5K+</span>
                    <span className="font-sans text-xs text-slate-500">Students Guided</span>
                  </div>
                  <div>
                    <span className="font-serif text-3xl font-bold text-[#991B1B] block">100%</span>
                    <span className="font-sans text-xs text-slate-500">Eggless Recipes</span>
                  </div>
                  <div>
                    <span className="font-serif text-3xl font-bold text-[#991B1B] block">30+</span>
                    <span className="font-sans text-xs text-slate-500">Festive Recipes</span>
                  </div>
                </div>
              </div>

            </div>
          </div>
        </section>

        {/* EXCLUSIVE BONUSES GRID */}
        <section id="bonuses" className="py-20 bg-[#FAFAFA] border-b border-[#E2E8F0]">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            
            <div className="text-center max-w-3xl mx-auto mb-16">
              <span className="text-xs uppercase tracking-[0.25em] text-[#991B1B] font-bold block mb-2">High Value Bundles</span>
              <h2 className="font-serif text-3xl sm:text-4xl font-bold text-[#0F172A] tracking-tight">
                Exclusive Bonuses Included!
              </h2>
              <p className="font-sans text-slate-500 mt-2">
                Get premium digital resources completely included with your ticket today.
              </p>
              <div className="w-12 h-1 bg-[#D97706] mx-auto mt-4 rounded"></div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {bonuses.map((bonus, idx) => {
                const IconComponent = bonus.icon;
                return (
                  <div key={idx} className="premium-card p-8 rounded-2xl bg-white text-left relative flex flex-col justify-between group">
                    <div>
                      <div className="w-12 h-12 bg-amber-50 rounded-xl flex items-center justify-center text-[#D97706] mb-5 group-hover:bg-[#D97706] group-hover:text-white transition-colors">
                        <IconComponent className="w-6 h-6" />
                      </div>
                      
                      <div className="flex justify-between items-start mb-3">
                        <h3 className="font-sans font-bold text-lg text-[#0F172A] pr-2">{bonus.title}</h3>
                      </div>

                      <p className="font-sans text-sm text-slate-500 leading-relaxed mb-6">
                        {bonus.desc}
                      </p>
                    </div>

                    <span className="inline-block self-start font-sans text-xs font-extrabold tracking-widest text-[#991B1B] uppercase bg-rose-50 px-3 py-1 rounded-full">
                      INCLUDED BONUS
                    </span>
                  </div>
                );
              })}
            </div>

            <div className="mt-16 bg-white border border-[#E2E8F0] p-8 sm:p-10 rounded-3xl max-w-4xl mx-auto shadow-sm flex flex-col md:flex-row items-center justify-between text-left">
              <div className="space-y-2 mb-6 md:mb-0">
                <span className="text-xs uppercase tracking-widest text-[#D97706] font-bold">100% Value Guarantee</span>
                <h3 className="font-serif font-bold text-[#0F172A] text-xl sm:text-2xl">Bake Beautifully or Your Money Back</h3>
                <p className="font-sans text-sm text-slate-500">If our recipes do not meet your expectations, reach out to our dedicated support.</p>
              </div>
              
              <button
                onClick={handleOpenModal}
                data-testid="register-button-bonuses"
                className="bg-[#991B1B] hover:bg-[#7F1D1D] text-white font-sans text-sm font-bold py-3.5 px-8 rounded-full transition-all transform active:scale-95 shrink-0 animate-pulse"
              >
                Claim All Bonuses
              </button>
            </div>

          </div>
        </section>

        {/* FAQs */}
        <section id="faq" className="py-20 bg-white border-b border-[#E2E8F0]">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
            
            <div className="text-center max-w-2xl mx-auto mb-12">
              <span className="text-xs uppercase tracking-[0.25em] text-[#991B1B] font-bold block mb-2">Have Questions?</span>
              <h2 className="font-serif text-3xl sm:text-4xl font-bold text-[#0F172A] tracking-tight">
                Frequently Asked Questions
              </h2>
              <div className="w-12 h-1 bg-[#D97706] mx-auto mt-4 rounded"></div>
            </div>

            <div className="bg-[#FAFAFA] border border-[#E2E8F0] p-6 sm:p-8 rounded-3xl shadow-sm space-y-2">
              {faqs.map((faq, idx) => (
                <FAQItem 
                  key={idx} 
                  question={faq.q} 
                  answer={faq.a} 
                  index={idx} 
                />
              ))}
            </div>

          </div>
        </section>
      </main>

      {/* FOOTER SECTION */}
      <footer className="bg-[#0F172A] text-white py-12 border-t border-[#1E293B]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col md:flex-row items-center justify-between space-y-6 md:space-y-0">
          
          <div className="flex items-center space-x-2 text-left animate-fadeIn">
            <span className="text-xl">🎄</span>
            <div className="flex flex-col">
              <span className="font-serif font-bold text-lg tracking-tight">CHEF ASHMIKA AGRAWAL</span>
              <span className="text-[9px] tracking-[0.25em] text-amber-500 font-bold uppercase">BAKING ACADEMY</span>
            </div>
          </div>

          <p className="font-sans text-xs text-slate-400">
            &copy; {new Date().getFullYear()} Chef Ashmika Agrawal. All rights reserved. Created with Mango AI.
          </p>

          <div className="flex space-x-6 text-xs text-slate-400">
            <a href="#curriculum" className="hover:text-white transition-colors">Curriculum</a>
            <a href="#bonuses" className="hover:text-white transition-colors">Bonuses</a>
            <a href="#faq" className="hover:text-white transition-colors">FAQs</a>
          </div>

        </div>
      </footer>

      {/* REGISTRATION POPUP MODAL */}
      {isModalOpen && (
        <div 
          className="fixed inset-0 z-50 overflow-y-auto flex items-center justify-center p-4 animate-fadeIn"
          role="dialog"
          aria-modal="true"
        >
          {/* Overlay */}
          <div 
            onClick={handleCloseModal} 
            className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm transition-opacity"
          ></div>

          {/* Modal body */}
          <div className="relative bg-white rounded-3xl max-w-lg w-full p-6 sm:p-10 shadow-2xl border border-slate-100 z-10 overflow-hidden animate-zoomIn">
            
            {/* Elegant non-clipping top right badge */}
            <div className="absolute top-5 right-5 bg-[#991B1B]/10 text-[#991B1B] text-[10px] font-extrabold tracking-widest uppercase py-1 px-3.5 rounded-full border border-[#991B1B]/20">
              WORKSHOP ACCESS
            </div>

            {/* Close Button */}
            <button
              onClick={handleCloseModal}
              data-testid="modal-close-button"
              className="absolute top-5 left-5 text-slate-400 hover:text-[#991B1B] focus:outline-none focus:ring-2 focus:ring-[#991B1B] focus:ring-offset-2 rounded-full p-1 transition-colors"
              aria-label="Close modal"
            >
              <X className="w-5 h-5" />
            </button>

            {!isSubmitted ? (
              <div className="text-left mt-4">
                
                <div className="mb-6 space-y-1">
                  <span className="font-sans text-[10px] uppercase tracking-[0.2em] font-extrabold text-[#D97706]">Secured Registration</span>
                  <h3 className="font-serif text-2xl sm:text-3xl font-bold text-[#0F172A]">Claim Your Workshop Seat</h3>
                </div>

                <form 
                  onSubmit={handleSubmit} 
                  data-testid="registration-form" 
                  className="space-y-4"
                >
                  
                  <div className="space-y-1.5">
                    <label className="font-sans text-xs font-bold text-slate-700 uppercase tracking-wider block">
                      Full Name
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      placeholder="Chef Ashmika Agrawal"
                      required
                      data-testid="input-full-name"
                      className="w-full font-sans text-base bg-slate-50 border border-slate-200 rounded-xl py-3 px-4 text-[#0F172A] placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#991B1B] focus:border-transparent transition-all"
                    />
                  </div>

                  <div className="space-y-1.5">
                    <label className="font-sans text-xs font-bold text-slate-700 uppercase tracking-wider block">
                      Email Address
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      placeholder="baker@domain.com"
                      required
                      data-testid="input-email"
                      className="w-full font-sans text-base bg-slate-50 border border-slate-200 rounded-xl py-3 px-4 text-[#0F172A] placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#991B1B] focus:border-transparent transition-all"
                    />
                  </div>

                  <div className="space-y-1.5">
                    <label className="font-sans text-xs font-bold text-slate-700 uppercase tracking-wider block">
                      WhatsApp Phone Number
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      placeholder="e.g. +91 98765 43210"
                      required
                      data-testid="input-phone"
                      className="w-full font-sans text-base bg-slate-50 border border-slate-200 rounded-xl py-3 px-4 text-[#0F172A] placeholder:text-slate-400 focus:outline-none focus:ring-2 focus:ring-[#991B1B] focus:border-transparent transition-all"
                    />
                  </div>

                  {/* Checklist of inclusions */}
                  <div className="p-4 bg-rose-50/50 rounded-2xl border border-rose-100 flex flex-col space-y-1.5 my-2">
                    <span className="text-[10px] font-extrabold uppercase tracking-widest text-[#991B1B]">INCLUDED WITH SEAT</span>
                    <ul className="space-y-1 text-xs text-slate-600 font-medium">
                      <li className="flex items-center"><Check className="w-3.5 h-3.5 text-[#991B1B] mr-1.5 shrink-0" /> Full 2-Day Online Workshop Access</li>
                      <li className="flex items-center"><Check className="w-3.5 h-3.5 text-[#991B1B] mr-1.5 shrink-0" /> Lifetime Video Recording Access</li>
                      <li className="flex items-center"><Check className="w-3.5 h-3.5 text-[#991B1B] mr-1.5 shrink-0" /> Printable Recipe eBook PDF Included</li>
                    </ul>
                  </div>

                  <button
                    type="submit"
                    data-testid="submit-registration-button"
                    className="w-full bg-[#991B1B] hover:bg-[#7F1D1D] text-white font-sans text-base font-bold py-3.5 px-6 rounded-xl transition-all duration-300 transform active:scale-95 shadow-lg shadow-[#991B1B]/15"
                  >
                    Confirm My Seat Reservation
                  </button>

                </form>

              </div>
            ) : (
              /* Success Card */
              <div className="text-center mt-6 space-y-6">
                
                <div className="mx-auto w-16 h-16 bg-[#25D366]/10 text-[#25D366] rounded-full flex items-center justify-center animate-bounce">
                  <Check className="w-8 h-8 stroke-[3]" />
                </div>

                <div className="space-y-2">
                  <span data-testid="success-badge" className="font-sans text-[11px] uppercase tracking-[0.2em] font-extrabold text-[#25D366]">Reservation Successful!</span>
                  <h3 data-testid="success-title" className="font-serif text-3xl font-bold text-[#0F172A]">Thank You, {formData.name}!</h3>
                </div>

                <div className="space-y-3">
                  <a
                    href={WHATSAPP_GROUP_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="whatsapp-redirect-button"
                    onClick={() => {
                      if (redirectTimeoutRef.current) {
                        clearTimeout(redirectTimeoutRef.current);
                      }
                    }}
                    className="w-full bg-[#25D366] hover:bg-[#128C7E] text-white font-sans text-base font-bold py-4 px-6 rounded-xl transition-all duration-300 transform active:scale-95 shadow-md hover:shadow-lg inline-flex items-center justify-center space-x-2"
                  >
                    <span>Join WhatsApp Support Group</span>
                    <ExternalLink className="w-5 h-5 shrink-0" />
                  </a>
                  
                  <button 
                    onClick={handleCloseModal}
                    data-testid="return-home-button"
                    className="text-xs text-slate-400 hover:text-slate-600 underline font-medium"
                  >
                    Return to landing page
                  </button>
                </div>

              </div>
            )}

          </div>
        </div>
      )}

    </div>
  );
}